import type { Section, SectionId, Subcategory } from '@/types/documents-library';

/**
 * Farm sections + subcategories.
 *
 * Structure sourced from the SME document "till digital pärm" (2026-07-03):
 * section labels come from its "Sektion 1 (läsbar)" column and subcategory
 * labels verbatim from "Sektion 2 (läsbar)". On sikt genereras strukturen
 * från kontrollprogrammen som matchar gårdsprofilen — until then this static
 * copy is the source of truth.
 *
 * Two sections are NOT in the SME document and keep their previous shape:
 * - `djur` — the document's Djurhållning rows are still empty.
 * - `cert` — certifikat/stöd har ingen hemvist i dokumentet men behövs för
 *   befintliga certifikat- och stödförslag (inkl. SAM-ansökan).
 *
 * IDs are hyphenated `<sectionId>-<localId>` to keep them globally unique.
 * Empty subcategories are skipped by the selector, so the full SME structure
 * can live here before every subcategory has suggestions or documents.
 */

export const SECTIONS: Record<SectionId, Section> = {
  allmanna: {
    id: 'allmanna',
    label: 'Allmänna gårdskrav',
    order: 0,
    subcategoryIds: [
      'allmanna-tillstand',
      'allmanna-egenkontroll',
      'allmanna-kemiska',
      'allmanna-cisterner',
      'allmanna-avfall',
      'allmanna-farligt-avfall',
      'allmanna-energi',
      'allmanna-kyl',
      'allmanna-producentansvar',
      'allmanna-brand',
      'allmanna-fornlamningar',
      'allmanna-invasiva',
      'allmanna-ovrigt',
    ],
  },
  vaxtodling: {
    id: 'vaxtodling',
    label: 'Växtodling',
    order: 1,
    subcategoryIds: [
      'vaxtodling-registrering',
      'vaxtodling-livsmedel',
      'vaxtodling-vaxtskydd',
      'vaxtodling-sparbarhet',
      'vaxtodling-markning',
      'vaxtodling-hostbevuxen',
      'vaxtodling-spridning',
      'vaxtodling-lagring',
      'vaxtodling-avloppsslam',
      'vaxtodling-gmo',
      'vaxtodling-vaxtskadegorare',
      'vaxtodling-abp',
      'vaxtodling-vattenverksamhet',
      'vaxtodling-samrad',
      'vaxtodling-natur-kultur',
      'vaxtodling-skydd-omraden',
      'vaxtodling-vilda',
      'vaxtodling-vaxtfoljd',
      'vaxtodling-vatmark',
    ],
  },
  djur: {
    id: 'djur',
    label: 'Djurhållning',
    order: 2,
    subcategoryIds: [
      'djur-journaler',
      'djur-foder',
      'djur-halsa',
      'djur-transport',
    ],
  },
  arbetsmiljo: {
    id: 'arbetsmiljo',
    label: 'Arbetsmiljö',
    order: 3,
    subcategoryIds: [
      'arbetsmiljo-sam',
      'arbetsmiljo-tillbud',
      'arbetsmiljo-bygg',
      'arbetsmiljo-teknik',
      'arbetsmiljo-skydd',
      'arbetsmiljo-kemikalier',
      'arbetsmiljo-djurarbete',
      'arbetsmiljo-motorsag',
      'arbetsmiljo-minderariga',
      'arbetsmiljo-barnsakerhet',
      'arbetsmiljo-gravida',
      'arbetsmiljo-trafik',
      'arbetsmiljo-sociala',
    ],
  },
  cert: {
    id: 'cert',
    label: 'Certifikat',
    order: 4,
    subcategoryIds: ['cert-certifikat', 'cert-stod', 'cert-radgivning'],
  },
};

export const SECTION_LIST: Section[] = Object.values(SECTIONS).sort(
  (a, b) => a.order - b.order,
);

export const SUBCATEGORIES: Record<string, Subcategory> = {
  // ALLMÄNNA GÅRDSKRAV
  'allmanna-tillstand': {
    id: 'allmanna-tillstand',
    sectionId: 'allmanna',
    label: 'Anmälnings- och tillståndsplikt',
    order: 0,
  },
  'allmanna-egenkontroll': {
    id: 'allmanna-egenkontroll',
    sectionId: 'allmanna',
    label: 'Egenkontroll av miljöpåverkan',
    order: 1,
  },
  'allmanna-kemiska': {
    id: 'allmanna-kemiska',
    sectionId: 'allmanna',
    label: 'Kemiska produkter',
    order: 2,
  },
  'allmanna-cisterner': {
    id: 'allmanna-cisterner',
    sectionId: 'allmanna',
    label:
      'Cisterner, rör och slangledningar där bensin, diesel eller oljor hanteras',
    order: 3,
  },
  'allmanna-avfall': {
    id: 'allmanna-avfall',
    sectionId: 'allmanna',
    label: 'Avfall',
    order: 4,
  },
  'allmanna-farligt-avfall': {
    id: 'allmanna-farligt-avfall',
    sectionId: 'allmanna',
    label: 'Farligt avfall',
    order: 5,
  },
  'allmanna-energi': {
    id: 'allmanna-energi',
    sectionId: 'allmanna',
    label: 'Energiproduktion',
    order: 6,
  },
  'allmanna-kyl': {
    id: 'allmanna-kyl',
    sectionId: 'allmanna',
    label: 'Kyl- värme- och klimatanläggningar',
    order: 7,
  },
  'allmanna-producentansvar': {
    id: 'allmanna-producentansvar',
    sectionId: 'allmanna',
    label: 'Producentansvar',
    order: 8,
  },
  'allmanna-brand': {
    id: 'allmanna-brand',
    sectionId: 'allmanna',
    label: 'Skydd mot brand',
    order: 9,
  },
  'allmanna-fornlamningar': {
    id: 'allmanna-fornlamningar',
    sectionId: 'allmanna',
    label:
      'Fornlämningar, fornfynd, byggnadsminnen och särskilt värdefulla byggnader och byggnadsverk',
    order: 10,
  },
  'allmanna-invasiva': {
    id: 'allmanna-invasiva',
    sectionId: 'allmanna',
    label: 'Invasiva arter',
    order: 11,
  },
  'allmanna-ovrigt': {
    id: 'allmanna-ovrigt',
    sectionId: 'allmanna',
    label: 'Övrigt',
    order: 12,
  },

  // VÄXTODLING
  'vaxtodling-registrering': {
    id: 'vaxtodling-registrering',
    sectionId: 'vaxtodling',
    label: 'Registrering av livsmedels- och foderanläggning samt godkännande',
    order: 0,
  },
  'vaxtodling-livsmedel': {
    id: 'vaxtodling-livsmedel',
    sectionId: 'vaxtodling',
    label: 'Livsmedelssäkerhet i växtodlingen/växtprodukter',
    order: 1,
  },
  'vaxtodling-vaxtskydd': {
    id: 'vaxtodling-vaxtskydd',
    sectionId: 'vaxtodling',
    label: 'Växtskydd, miljö-, livsmedels- och fodersäkerhet',
    order: 2,
  },
  'vaxtodling-sparbarhet': {
    id: 'vaxtodling-sparbarhet',
    sectionId: 'vaxtodling',
    label: 'Spårbarhet i växtodlingen',
    order: 3,
  },
  'vaxtodling-markning': {
    id: 'vaxtodling-markning',
    sectionId: 'vaxtodling',
    label: 'Märkning och presentation av livsmedel',
    order: 4,
  },
  'vaxtodling-hostbevuxen': {
    id: 'vaxtodling-hostbevuxen',
    sectionId: 'vaxtodling',
    label: 'Höstbevuxen mark (grön mark)',
    order: 5,
  },
  'vaxtodling-spridning': {
    id: 'vaxtodling-spridning',
    sectionId: 'vaxtodling',
    label:
      'Spridning av mineralgödsel, stallgödsel och andra organiska gödselmedel',
    order: 6,
  },
  'vaxtodling-lagring': {
    id: 'vaxtodling-lagring',
    sectionId: 'vaxtodling',
    label: 'Lagring av stallgödsel',
    order: 7,
  },
  'vaxtodling-avloppsslam': {
    id: 'vaxtodling-avloppsslam',
    sectionId: 'vaxtodling',
    label: 'Avloppsslam',
    order: 8,
  },
  'vaxtodling-gmo': {
    id: 'vaxtodling-gmo',
    sectionId: 'vaxtodling',
    label: 'GMO',
    order: 9,
  },
  'vaxtodling-vaxtskadegorare': {
    id: 'vaxtodling-vaxtskadegorare',
    sectionId: 'vaxtodling',
    label: 'Reglering av vissa växtskadegörare och flyghavre',
    order: 10,
  },
  'vaxtodling-abp': {
    id: 'vaxtodling-abp',
    sectionId: 'vaxtodling',
    label: 'Animaliska biprodukter samt aska',
    order: 11,
  },
  'vaxtodling-vattenverksamhet': {
    id: 'vaxtodling-vattenverksamhet',
    sectionId: 'vaxtodling',
    label: 'Vattenverksamhet',
    order: 12,
  },
  'vaxtodling-samrad': {
    id: 'vaxtodling-samrad',
    sectionId: 'vaxtodling',
    label: 'Samråd om åtgärder som väsentligt kan ändra naturmiljön',
    order: 13,
  },
  'vaxtodling-natur-kultur': {
    id: 'vaxtodling-natur-kultur',
    sectionId: 'vaxtodling',
    label: 'Hänsyn till natur- och kulturvärden (landskapselement)',
    order: 14,
  },
  'vaxtodling-skydd-omraden': {
    id: 'vaxtodling-skydd-omraden',
    sectionId: 'vaxtodling',
    label: 'Skydd av områden',
    order: 15,
  },
  'vaxtodling-vilda': {
    id: 'vaxtodling-vilda',
    sectionId: 'vaxtodling',
    label: 'Skydd av vilda djur och växter',
    order: 16,
  },
  'vaxtodling-vaxtfoljd': {
    id: 'vaxtodling-vaxtfoljd',
    sectionId: 'vaxtodling',
    label: 'Växtföljd',
    order: 17,
  },
  'vaxtodling-vatmark': {
    id: 'vaxtodling-vatmark',
    sectionId: 'vaxtodling',
    label: 'Skydd av våt- och torvmark',
    order: 18,
  },

  // DJURHÅLLNING — ej ifylld i SME-dokumentet ännu; behåller tidigare struktur
  'djur-journaler': {
    id: 'djur-journaler',
    sectionId: 'djur',
    label: 'Journaler & register',
    order: 0,
  },
  'djur-foder': {
    id: 'djur-foder',
    sectionId: 'djur',
    label: 'Foder & gödsel',
    order: 1,
  },
  'djur-halsa': {
    id: 'djur-halsa',
    sectionId: 'djur',
    label: 'Hälsa & beredskap',
    order: 2,
  },
  'djur-transport': {
    id: 'djur-transport',
    sectionId: 'djur',
    label: 'Transport & slakt',
    order: 3,
  },

  // ARBETSMILJÖ
  'arbetsmiljo-sam': {
    id: 'arbetsmiljo-sam',
    sectionId: 'arbetsmiljo',
    label: 'Systematiskt arbetsmiljöarbete',
    order: 0,
  },
  'arbetsmiljo-tillbud': {
    id: 'arbetsmiljo-tillbud',
    sectionId: 'arbetsmiljo',
    label: 'Anmälan av tillbud och olycksfall',
    order: 1,
  },
  'arbetsmiljo-bygg': {
    id: 'arbetsmiljo-bygg',
    sectionId: 'arbetsmiljo',
    label:
      'Arbetsmiljön vid byggnads-, reparations-, rivnings och anläggningsarbete',
    order: 2,
  },
  'arbetsmiljo-teknik': {
    id: 'arbetsmiljo-teknik',
    sectionId: 'arbetsmiljo',
    label: 'Tekniska anordningar och säkerhet',
    order: 3,
  },
  'arbetsmiljo-skydd': {
    id: 'arbetsmiljo-skydd',
    sectionId: 'arbetsmiljo',
    label: 'Skyddsutrustning och första hjälpen',
    order: 4,
  },
  'arbetsmiljo-kemikalier': {
    id: 'arbetsmiljo-kemikalier',
    sectionId: 'arbetsmiljo',
    label: 'Arbetsrisker med kemikalier och gaser',
    order: 5,
  },
  'arbetsmiljo-djurarbete': {
    id: 'arbetsmiljo-djurarbete',
    sectionId: 'arbetsmiljo',
    label: 'Arbete med djur',
    order: 6,
  },
  'arbetsmiljo-motorsag': {
    id: 'arbetsmiljo-motorsag',
    sectionId: 'arbetsmiljo',
    label: 'Arbetsmiljö vid arbete med motorsåg och röjsåg',
    order: 7,
  },
  'arbetsmiljo-minderariga': {
    id: 'arbetsmiljo-minderariga',
    sectionId: 'arbetsmiljo',
    label: 'Minderåriga',
    order: 8,
  },
  'arbetsmiljo-barnsakerhet': {
    id: 'arbetsmiljo-barnsakerhet',
    sectionId: 'arbetsmiljo',
    label: 'Barnsäkerhet',
    order: 9,
  },
  'arbetsmiljo-gravida': {
    id: 'arbetsmiljo-gravida',
    sectionId: 'arbetsmiljo',
    label: 'Gravida och ammande arbetstagare',
    order: 10,
  },
  'arbetsmiljo-trafik': {
    id: 'arbetsmiljo-trafik',
    sectionId: 'arbetsmiljo',
    label: 'Trafik',
    order: 11,
  },
  'arbetsmiljo-sociala': {
    id: 'arbetsmiljo-sociala',
    sectionId: 'arbetsmiljo',
    label: 'Sociala grundvillkor',
    order: 12,
  },

  // CERTIFIKAT — ej del av SME-dokumentet; behåller tidigare struktur
  'cert-certifikat': {
    id: 'cert-certifikat',
    sectionId: 'cert',
    label: 'Certifikat',
    order: 0,
  },
  'cert-stod': {
    id: 'cert-stod',
    sectionId: 'cert',
    label: 'Stöd & ersättning',
    order: 1,
  },
  'cert-radgivning': {
    id: 'cert-radgivning',
    sectionId: 'cert',
    label: 'Rådgivning',
    order: 2,
  },
};

export function getSubcategory(id: string): Subcategory | undefined {
  return SUBCATEGORIES[id];
}

/** Fallback subcategory for documents whose category doesn't map elsewhere. */
export const FALLBACK_SUBCATEGORY_ID = 'allmanna-ovrigt';
